import React from 'react';
import {
  Upload,
  FileText,
  Check,
  ArrowRight
} from 'lucide-react';

const STEPS = {
  FILE_UPLOAD: 'file_upload',
  COLUMN_MAPPING: 'column_mapping',
  PREVIEW_IMPORT: 'preview_import'
};

const GenericImporterStepperProgressTracker = ({ activeStep, onStepClick }) => {
  const steps = [
    {
      id: 1,
      key: STEPS.FILE_UPLOAD,
      title: 'Ladda upp fil',
      description: 'Välj CSV-fil att importera',
      icon: Upload
    },
    {
      id: 2,
      key: STEPS.COLUMN_MAPPING,
      title: 'Mappa kolumner',
      description: 'Koppla CSV-kolumner till systemfält',
      icon: ArrowRight
    },
    {
      id: 3,
      key: STEPS.PREVIEW_IMPORT,
      title: 'Förhandsgranska & Importera',
      description: 'Kontrollera och slutför import',
      icon: FileText
    }
  ];

  const getCurrentStep = () => {
    if (activeStep === STEPS.FILE_UPLOAD) return 1;
    if (activeStep === STEPS.COLUMN_MAPPING) return 2;
    if (activeStep === STEPS.PREVIEW_IMPORT) return 3;
    return 1;
  };

  const getStatus = (step) => {
    const currentStep = getCurrentStep();
    if (currentStep > step.id) return 'completed';
    if (currentStep === step.id) return 'current';
    return 'pending';
  };

  const isStepAccessible = (stepId) => {
    return stepId <= getCurrentStep();
  };

  return (
    <>
      <style>{`
        .gi-progress-tracker {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
        }

        .gi-step-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          font-weight: 600;
          font-size: 0.875rem;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }

        .gi-step-indicator.completed {
          background: var(--bs-success);
          color: white;
        }

        .gi-step-indicator.current {
          background: var(--bs-primary);
          color: white;
          animation: gi-pulse 2s infinite;
        }

        .gi-step-indicator.pending {
          background: #f3f4f6;
          color: #6b7280;
        }

        .gi-step-connector {
          flex: 1;
          height: 2px;
          background: #e5e7eb;
          margin: 0 0.75rem;
          transition: background 0.3s ease;
        }

        .gi-step-connector.completed {
          background: var(--bs-primary);
        }

        @keyframes gi-pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(68, 129, 78, 0.4);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(68, 129, 78, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(68, 129, 78, 0);
          }
        }

        .gi-step-block {
          cursor: default;
        }

        .gi-step-block.accessible {
          cursor: pointer;
        }

        .gi-step-block.accessible:hover .gi-step-label {
          color: var(--bs-primary);
        }

        .gi-step-label {
          font-weight: 600;
          font-size: 0.875rem;
          color: #1e293b;
          transition: color 0.2s ease;
        }

        .gi-step-sublabel {
          font-size: 0.75rem;
          color: #6b7280;
        }

        .gi-step-block.completed .gi-step-label {
          color: #065f46;
        }

        .gi-step-block.current .gi-step-label {
          color: var(--bs-primary);
        }

        .gi-step-block.pending .gi-step-label {
          color: #9ca3af;
        }

        .gi-step-block.pending .gi-step-sublabel {
          color: #d1d5db;
        }

        @media (max-width: 768px) {
          .gi-steps-row {
            flex-direction: column !important;
            gap: 0.5rem;
          }

          .gi-step-connector {
            display: none;
          }

          .gi-step-block {
            background: #f8fafc;
            border-radius: 8px;
            border: 1px solid #e5e7eb;
            padding: 0.75rem 1rem;
          }
        }
      `}</style>

      <div className="gi-progress-tracker">
        <div className="d-flex align-items-center justify-content-between gi-steps-row">
          {steps.map((step, index) => {
            const status = getStatus(step);
            const accessible = isStepAccessible(step.id);
            const IconComponent = step.icon;

            return (
              <React.Fragment key={step.key}>
                <div
                  className={`d-flex align-items-center gi-step-block ${status} ${accessible ? 'accessible' : ''}`}
                  onClick={() => accessible && onStepClick && onStepClick(step.key)}
                >
                  <div className={`gi-step-indicator ${status}`}>
                    {status === 'completed' ? (
                      <Check size={20} />
                    ) : (
                      <IconComponent size={18} />
                    )}
                  </div>
                  <div className="ms-3">
                    <div className="gi-step-label">{step.title}</div>
                    <div className="gi-step-sublabel">{step.description}</div>
                  </div>
                </div>

                {index < steps.length - 1 && (
                  <div className={`gi-step-connector ${status === 'completed' ? 'completed' : ''}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default GenericImporterStepperProgressTracker;
